// ─────────────────────────────────────────────────────────────────
// modeConfig.js — Mode Policy Authority Layer
//
// This is the SINGLE SOURCE OF TRUTH for:
//   - Mode definitions and allowed output schemas
//   - Override thresholds (config constants, NOT decision logic)
//   - Mode transition rules
//
// Domain ownership:
//   modeConfig.js    → policy definitions ONLY
//   useLearningEngine → resolveMode() reads these constants
//   outputEnforcer   → enforces these schemas
//   promptCompiler   → compiles mode-aware instructions
//
// Nothing in this file contains runtime logic or state.
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────
// LEARNING MODES
// ─────────────────────────────────────────────
export const MODES = {
  LESSON:   'LESSON',
  DRILL:    'DRILL',
  REVIEW:   'REVIEW',
  COMPRESS: 'COMPRESS',
  PROFILE:  'PROFILE',
};

// ─────────────────────────────────────────────
// MODE SCHEMAS (strict output contracts)
// Used by outputEnforcer to validate LLM output.
// "allowed" = sections that may appear
// "required" = sections that must appear
// "forbidden" = sections that must be stripped
// ─────────────────────────────────────────────
export const MODE_SCHEMAS = {

  LESSON: {
    allowed:  ['context', 'compression', 'framework', 'scenario', 'checkpoint', 'navigation', 'mastery_signal'],
    required: [],
    forbidden: [],
    description: 'Full instructional delivery — Teaching Episode through practice. Checkpoint is optional, triggered by planner signal.',
  },

  DRILL: {
    allowed:  ['scenario', 'question'],
    required: ['scenario'],
    forbidden: ['explanation', 'lesson_context', 'mastery_signal', 'navigation', 'teaching'],
    description: 'Scenario and question only. No teaching. No explanation.',
  },

  REVIEW: {
    allowed:  ['context', 'compression', 'examples', 'scenario'],
    required: ['examples'],
    forbidden: ['advanced_scenarios', 'mastery_signal'],
    max_complexity: 0.5,
    description: 'Simplified re-teaching with more examples. Lower complexity ceiling.',
  },

  COMPRESS: {
    allowed:  ['compression_only'],
    required: ['compression_only'],
    forbidden: ['context', 'framework', 'scenario', 'navigation', 'mastery_signal', 'explanations', 'teaching'],
    description: 'Pure cognitive encoding. Mental models only. No explanations, no navigation.',
  },

  PROFILE: {
    allowed:  ['questions', 'track_overview', 'roadmap', 'lesson_preview'],
    required: ['questions'],
    forbidden: ['lesson_content', 'checkpoints'],
    description: 'Onboarding only. Goal, track selection, timeline. No lesson content.',
  },
};

// ─────────────────────────────────────────────
// OVERRIDE THRESHOLDS
// Config constants ONLY — not decision logic.
// resolveMode() in the engine reads these.
// Adjust here to tune pedagogy without touching logic.
// ─────────────────────────────────────────────
export const OVERRIDE_THRESHOLDS = {

  // Engine forces REVIEW when learner is struggling
  force_review: {
    repeated_failures:    3,     // consecutive checkpoint failures
    mastery_below:        0.40,  // mastery score floor
  },

  // Engine forces LESSON when learner requests DRILL/COMPRESS too early
  force_lesson: {
    mastery_below:        0.55,  // not ready for drill/compress yet
  },

  // Engine allows COMPRESS when learner is advanced
  allow_compress: {
    mastery_above:        0.80,
    stability_above:      0.75,
  },

  // Engine allows DRILL when learner has baseline understanding
  allow_drill: {
    mastery_above:        0.55,
    checkpoints_passed:   2,     // at least 2 checkpoints passed in this lesson
  },
};

// ─────────────────────────────────────────────
// MODE INSTRUCTIONS
// Used by promptCompiler to shape mode-specific system prompt sections.
// Each entry is a natural language instruction block per mode.
// ─────────────────────────────────────────────
export const MODE_INSTRUCTIONS = {

  LESSON: `Teaching mode. Deliver the CTA teaching rhythm: explanation, analogy, examples, practice, consolidation. Do not force a checkpoint on every lesson turn. Structured checkpoint is triggered only when the planner indicates checkpoint readiness via checkpoint_signal. Teaching turns may end without a question.`,

  DRILL: `Drill mode. ONLY output a scenario and question. No teaching. No explanation. No mastery signal. No navigation.
Format: Present a realistic situation. Ask "What is the BEST FIRST step?" or equivalent. Wait for learner answer.
If the learner answers correctly, present the next drill immediately.
If incorrectly, evaluate with JSON block and present a simplified retry. Do not reteach unless the learner asks.`,

  REVIEW: `Review mode. Reteach the current concept at reduced complexity. Use more examples. Use simpler language.
Maximum complexity: 50% of lesson mode depth.
Include at least 2 concrete examples before any scenario.
Do not include advanced scenarios or mastery signals.`,

  COMPRESS: `Compression mode. Output ONLY compressed mental models. Nothing else.
Format strictly as: CONCEPT = one-line model
Example output:
CPU = Execution Engine
RAM = Active Workspace
Storage = Persistent Memory
Motherboard = System Interconnect
No explanations. No context. No scenarios. No navigation. No mastery signals. No teaching.
Every line must be in the CONCEPT = model format.`,

  PROFILE: `Profile collection mode. You are meeting this learner for the first time.
Deliver: certification overview, 3-track roadmap, Lesson 1 preview.
Ask three questions: IT level, timeline, primary goal.
No lesson content. No checkpoints. Output [PROFILE_DONE] when profile is complete.`,
};

// ─────────────────────────────────────────────
// DEFAULT MODE
// Used as fallback when resolveMode() cannot determine effective mode.
// ─────────────────────────────────────────────
export const DEFAULT_MODE = MODES.LESSON;

// ─────────────────────────────────────────────
// STATE ARBITRATION HIERARCHY
// Defines explicit precedence when emotional, mastery, and mode states conflict.
// resolveMode() reads this — no implicit resolution anywhere.
//
// Priority order (highest wins):
//   1. Critical emotional state (PANIC, SHAME, COGNITIVE_OVERLOAD)
//   2. Repeated failures / low mastery → force REVIEW
//   3. Mode gate thresholds (COMPRESS/DRILL readiness)
//   4. User mode preference
//   5. Default mode
// ─────────────────────────────────────────────
export const STATE_ARBITRATION = {

  // Tier 1: Critical emotional states override ALL mode preferences.
  // These states indicate psychological safety is at risk.
  // The learning environment must stabilize before content resumes.
  critical_emotional: {
    states:      ['PANIC', 'SHAME', 'COGNITIVE_OVERLOAD'],
    force_mode:  MODES.REVIEW,
    reason:      'Critical emotional state detected — stabilizing before content.',
  },

  // Tier 2: Elevated emotional states restrict advanced modes.
  // Learner can still progress in LESSON but not COMPRESS or DRILL.
  restricted_emotional: {
    states:      ['FATIGUE', 'CONFUSION', 'MOTIVATION_DIP'],
    block_modes: [MODES.COMPRESS, MODES.DRILL],
    fallback:    MODES.LESSON,
    reason:      'Learner state not ready for advanced mode — returning to Lesson.',
  },

  // Tier 3: OVERCONFIDENCE should not block mode, but gates compression.
  // Handled by existing allow_compress threshold in OVERRIDE_THRESHOLDS.
  // Listed here for documentation clarity.
  overconfidence_note: 'OVERCONFIDENCE does not block mode — calibration happens via checkpoint difficulty.',
};

// ─────────────────────────────────────────────
// TEACHING PATTERNS
// Instructional texture rotation — prevents recognizable AI cadence.
// The compiler samples from this list and injects the active pattern
// as a session-level teaching preference. Pattern rotates per lesson.
//
// Each pattern describes a preferred OPENING move for that exchange,
// not a rigid template. The five-layer CTA architecture still governs
// the full response — this shapes how it begins.
// ─────────────────────────────────────────────
export const TEACHING_PATTERNS = [
  {
    id:          'analogy_first',
    label:       'Analogy First',
    instruction: 'Open with an analogy that makes the concept intuitive before introducing any technical detail.',
  },
  {
    id:          'troubleshooting_first',
    label:       'Troubleshooting First',
    instruction: 'Open with a failure scenario. What breaks when this concept is misunderstood? Work backward from the problem to the explanation.',
  },
  {
    id:          'question_first',
    label:       'Question First',
    instruction: 'Open with a probing question that surfaces what the learner already believes about this concept before teaching anything.',
  },
  {
    id:          'misconception_first',
    label:       'Misconception First',
    instruction: 'Open by naming the most common misconception about this concept. Correct it, then build the accurate mental model.',
  },
  {
    id:          'scenario_first',
    label:       'Scenario First',
    instruction: 'Open with a realistic scenario that requires this concept to resolve. Let the learner encounter the need before the explanation.',
  },
  {
    id:          'compare_contrast',
    label:       'Compare and Contrast',
    instruction: 'Open by placing this concept alongside a closely related one. Define both by how they differ, not just what they are.',
  },
  {
    id:          'visual_reasoning',
    label:       'Visual Reasoning',
    instruction: 'Open with a spatial or structural description — how does this concept look if you draw it? Build the mental image before the words.',
  },
];
 
